/**
 * Checkin Hooks
 *
 * Custom hooks para o módulo tmf_checkin.
 * Suporta check-in online/offline com sincronização em lote.
 *
 * @since 2025-12-17
 * @module tmf_checkin
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { checkinApi, generateNonce, formatDistance } from '@/services/checkinApi';
import { getJwtToken } from '@/services/apiClient';
import type {
  Checkin,
  CheckinListParams,
  CreateCheckinData,
  VisitCheckoutData,
  NearbyLocation,
  NearbySearchParams,
  CheckinStatistics,
  GeoPosition,
  GeolocationError,
  OfflineCheckin,
  BatchCheckinResult,
} from '@/types/checkin';

// Alias para manter compatibilidade
type CheckoutData = VisitCheckoutData;

// ============================================
// TYPES
// ============================================

interface UseListState<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  total: number;
}

interface UseItemState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseMutationState {
  loading: boolean;
  error: string | null;
}

// ============================================
// GEOLOCATION HOOK
// ============================================

interface UseGeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  watch?: boolean;
  /** Se true, solicita a posição automaticamente ao montar. Default: false */
  autoRequest?: boolean;
}

interface UseGeolocationState {
  position: GeoPosition | null;
  loading: boolean;
  error: GeolocationError | null;
  supported: boolean;
  /** Se a permissão já foi solicitada */
  requested: boolean;
}

/**
 * Hook para obter localização GPS do dispositivo
 *
 * Por padrão, NÃO solicita a posição automaticamente.
 * Chame `refresh()` para solicitar a permissão e obter a posição.
 */
export function useGeolocation(options: UseGeolocationOptions = {}) {
  const {
    enableHighAccuracy = true,
    timeout = 10000,
    maximumAge = 0,
    watch = false,
    autoRequest = false,
  } = options;

  const [state, setState] = useState<UseGeolocationState>({
    position: null,
    loading: false, // Não está carregando até ser solicitado
    error: null,
    supported: typeof navigator !== 'undefined' && 'geolocation' in navigator,
    requested: false,
  });

  const watchIdRef = useRef<number | null>(null);

  const handleSuccess = useCallback((position: GeolocationPosition) => {
    setState({
      position: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
      },
      loading: false,
      error: null,
      supported: true,
      requested: true,
    });
  }, []);

  const handleError = useCallback((error: GeolocationPositionError) => {
    setState((prev) => ({
      ...prev,
      loading: false,
      error: {
        code: error.code,
        message: error.message,
      },
      requested: true,
    }));
  }, []);

  const getPosition = useCallback(() => {
    if (!state.supported) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: { code: 0, message: 'Geolocalização não suportada' },
        requested: true,
      }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null, requested: true }));

    navigator.geolocation.getCurrentPosition(handleSuccess, handleError, {
      enableHighAccuracy,
      timeout,
      maximumAge,
    });
  }, [state.supported, handleSuccess, handleError, enableHighAccuracy, timeout, maximumAge]);

  useEffect(() => {
    if (!state.supported) return;
    // Só solicita automaticamente se autoRequest for true
    if (!autoRequest) return;

    if (watch) {
      // Watch position continuously
      watchIdRef.current = navigator.geolocation.watchPosition(
        handleSuccess,
        handleError,
        { enableHighAccuracy, timeout, maximumAge }
      );
    } else {
      // Get position once
      getPosition();
    }

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [watch, state.supported, autoRequest]);

  return { ...state, refresh: getPosition };
}

// ============================================
// CHECKIN LIST HOOK
// ============================================

/**
 * Hook para lista de check-ins
 */
export function useCheckinList(params: CheckinListParams = {}) {
  const [state, setState] = useState<UseListState<Checkin>>({
    data: [],
    loading: true,
    error: null,
    page: params.page || 1,
    totalPages: 0,
    total: 0,
  });

  const fetchCheckins = useCallback(async (fetchParams: CheckinListParams = {}) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await checkinApi.list(fetchParams);
      setState({
        data: response.data,
        loading: false,
        error: null,
        page: response.page,
        totalPages: response.pages,
        total: response.total,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Erro ao carregar check-ins',
      }));
    }
  }, []);

  useEffect(() => {
    fetchCheckins(params);
  }, [JSON.stringify(params)]);

  const setPage = useCallback(
    (page: number) => {
      fetchCheckins({ ...params, page });
    },
    [params, fetchCheckins]
  );

  return { ...state, refresh: () => fetchCheckins(params), setPage };
}

// ============================================
// ACTIVE CHECKIN HOOK
// ============================================

/**
 * Hook para obter check-in ativo do usuário atual
 * Nota: Requer token JWT para funcionar com a API Python
 */
export function useActiveCheckin(personId: number | null) {
  const [state, setState] = useState<UseItemState<Checkin>>({
    data: null,
    loading: false, // Começa como false até verificar se pode buscar
    error: null,
  });

  const fetchActiveCheckin = useCallback(async () => {
    // Verificar se há token JWT válido antes de fazer a chamada
    const token = getJwtToken();
    if (!personId || !token) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await checkinApi.getActive({ person_id: personId });
      // Retorna o primeiro check-in ativo (deve ter apenas 1)
      const activeCheckin = response.data.length > 0 ? response.data[0] : null;
      setState({ data: activeCheckin, loading: false, error: null });
    } catch (err) {
      // Silenciar erros de autenticação/API não disponível
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar check-in ativo';
      // Não logar erro se for 401/403/500 (API não pronta ou sem auth)
      setState({
        data: null,
        loading: false,
        error: null, // Não mostrar erro para usuário
      });
    }
  }, [personId]);

  useEffect(() => {
    fetchActiveCheckin();
  }, [fetchActiveCheckin]);

  return { ...state, refresh: fetchActiveCheckin };
}

// ============================================
// CHECKIN MUTATION HOOK
// ============================================

/**
 * Hook para operações de check-in/checkout
 */
export function useCheckinMutation() {
  const [state, setState] = useState<UseMutationState>({
    loading: false,
    error: null,
  });

  const checkin = useCallback(
    async (data: Omit<CreateCheckinData, 'nonce'>): Promise<Checkin> => {
      setState({ loading: true, error: null });
      try {
        const checkinData: CreateCheckinData = {
          ...data,
          nonce: generateNonce(),
        };
        const response = await checkinApi.create(checkinData);
        setState({ loading: false, error: null });
        return response;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao fazer check-in';
        setState({ loading: false, error: errorMessage });
        throw err;
      }
    },
    []
  );

  const checkout = useCallback(async (id: number, data: CheckoutData): Promise<Checkin> => {
    setState({ loading: true, error: null });
    try {
      const response = await checkinApi.checkout(id, data);
      setState({ loading: false, error: null });
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao fazer checkout';
      setState({ loading: false, error: errorMessage });
      throw err;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ loading: false, error: null });
  }, []);

  return { ...state, checkin, checkout, reset };
}

// ============================================
// NEARBY LOCATIONS HOOK
// ============================================

interface UseNearbyLocationsState {
  locations: NearbyLocation[];
  loading: boolean;
  error: string | null;
  lastSearch: NearbySearchParams | null;
}

/**
 * Hook para buscar locais próximos
 */
export function useNearbyLocations() {
  const [state, setState] = useState<UseNearbyLocationsState>({
    locations: [],
    loading: false,
    error: null,
    lastSearch: null,
  });

  const searchNearby = useCallback(async (params: NearbySearchParams): Promise<NearbyLocation[]> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const locations = await checkinApi.getNearby(params);
      setState({
        locations,
        loading: false,
        error: null,
        lastSearch: params,
      });
      return locations;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar locais próximos';
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      throw err;
    }
  }, []);

  const searchByText = useCallback(async (search: string, limit: number = 20): Promise<NearbyLocation[]> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const locations = await checkinApi.searchLocations(search, limit);
      setState({
        locations,
        loading: false,
        error: null,
        lastSearch: null,
      });
      return locations;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar locais';
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      throw err;
    }
  }, []);

  const reset = useCallback(() => {
    setState({
      locations: [],
      loading: false,
      error: null,
      lastSearch: null,
    });
  }, []);

  return { ...state, searchNearby, searchByText, reset };
}

// ============================================
// STATISTICS HOOK
// ============================================

/**
 * Hook para estatísticas de check-in
 */
export function useCheckinStatistics(params: CheckinListParams = {}) {
  const [state, setState] = useState<UseItemState<CheckinStatistics>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchStatistics = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const stats = await checkinApi.getStatistics(params);
      setState({ data: stats, loading: false, error: null });
    } catch (err) {
      setState({
        data: null,
        loading: false,
        error: err instanceof Error ? err.message : 'Erro ao carregar estatísticas',
      });
    }
  }, [JSON.stringify(params)]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  return { ...state, refresh: fetchStatistics };
}

// ============================================
// OFFLINE CHECKINS HOOK
// ============================================

const OFFLINE_STORAGE_KEY = 'tmf_offline_checkins';

/**
 * Hook para gerenciar check-ins offline
 */
export function useOfflineCheckins() {
  const [offlineCheckins, setOfflineCheckins] = useState<OfflineCheckin[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Load offline checkins from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(OFFLINE_STORAGE_KEY);
      if (stored) {
        setOfflineCheckins(JSON.parse(stored));
      }
    } catch (err) {
      console.error('Erro ao carregar check-ins offline:', err);
    }
  }, []);

  // Save to localStorage when offlineCheckins changes
  useEffect(() => {
    try {
      localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(offlineCheckins));
    } catch (err) {
      console.error('Erro ao salvar check-ins offline:', err);
    }
  }, [offlineCheckins]);

  /**
   * Adiciona check-in offline para sincronização posterior
   */
  const addOfflineCheckin = useCallback((data: Omit<CreateCheckinData, 'client_id' | 'nonce'>) => {
    const offlineCheckin: OfflineCheckin = {
      ...data,
      client_id: crypto.randomUUID(),
      nonce: generateNonce(),
      created_at_local: new Date().toISOString(),
      sync_status: 'pending',
      retry_count: 0,
    };

    setOfflineCheckins((prev) => [...prev, offlineCheckin]);
    return offlineCheckin.client_id;
  }, []);

  /**
   * Remove check-in offline
   */
  const removeOfflineCheckin = useCallback((clientId: string) => {
    setOfflineCheckins((prev) => prev.filter((c) => c.client_id !== clientId));
  }, []);

  /**
   * Sincroniza check-ins offline com o servidor
   */
  const syncOfflineCheckins = useCallback(async (): Promise<BatchCheckinResult | null> => {
    const pendingCheckins = offlineCheckins.filter(
      (c) => c.sync_status === 'pending' || c.sync_status === 'error'
    );

    if (pendingCheckins.length === 0) {
      return null;
    }

    setSyncing(true);
    setSyncError(null);

    // Mark as syncing
    setOfflineCheckins((prev) =>
      prev.map((c) =>
        pendingCheckins.some((p) => p.client_id === c.client_id)
          ? { ...c, sync_status: 'syncing' as const }
          : c
      )
    );

    try {
      const result = await checkinApi.batchSync({
        checkins: pendingCheckins.map((c) => ({
          id_person: c.id_person,
          id_resource: c.id_resource,
          datetime_checkin: c.datetime_checkin,
          latitude: c.latitude,
          longitude: c.longitude,
          observation: c.observation,
          source: c.source,
          nonce: c.nonce,
          client_id: c.client_id,
        })),
        auto_checkout: true,
        continue_on_error: true,
      });

      // Update offline checkins based on results
      setOfflineCheckins((prev) =>
        prev.map((c) => {
          const itemResult = result.results.find((r) => r.client_id === c.client_id);
          if (!itemResult) return c;

          if (itemResult.success) {
            return { ...c, sync_status: 'synced' as const };
          } else {
            return {
              ...c,
              sync_status: 'error' as const,
              sync_error: itemResult.error || 'Erro desconhecido',
              retry_count: c.retry_count + 1,
            };
          }
        })
      );

      // Remove synced items after a delay
      setTimeout(() => {
        setOfflineCheckins((prev) => prev.filter((c) => c.sync_status !== 'synced'));
      }, 2000);

      setSyncing(false);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao sincronizar';
      setSyncError(errorMessage);

      // Mark all as error
      setOfflineCheckins((prev) =>
        prev.map((c) =>
          c.sync_status === 'syncing'
            ? { ...c, sync_status: 'error' as const, sync_error: errorMessage, retry_count: c.retry_count + 1 }
            : c
        )
      );

      setSyncing(false);
      throw err;
    }
  }, [offlineCheckins]);

  /**
   * Limpa todos os check-ins offline
   */
  const clearOfflineCheckins = useCallback(() => {
    setOfflineCheckins([]);
    localStorage.removeItem(OFFLINE_STORAGE_KEY);
  }, []);

  const pendingCount = offlineCheckins.filter(
    (c) => c.sync_status === 'pending' || c.sync_status === 'error'
  ).length;

  return {
    offlineCheckins,
    pendingCount,
    syncing,
    syncError,
    addOfflineCheckin,
    removeOfflineCheckin,
    syncOfflineCheckins,
    clearOfflineCheckins,
  };
}

// ============================================
// COMBINED CHECKIN HOOK
// ============================================

interface UseCheckinOptions {
  personId: number;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

/**
 * Hook combinado para check-in completo
 *
 * Combina geolocalização, check-in ativo e mutações em um único hook.
 */
export function useCheckin(options: UseCheckinOptions) {
  const { personId, autoRefresh = false, refreshInterval = 30000 } = options;

  // Geolocation
  const geolocation = useGeolocation({ enableHighAccuracy: true });

  // Active checkin
  const activeCheckin = useActiveCheckin(personId);

  // Mutations
  const mutation = useCheckinMutation();

  // Nearby locations
  const nearbyLocations = useNearbyLocations();

  // Offline support
  const offline = useOfflineCheckins();

  // Auto refresh active checkin
  useEffect(() => {
    if (!autoRefresh || !personId) return;

    const interval = setInterval(() => {
      activeCheckin.refresh();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, personId, refreshInterval, activeCheckin.refresh]);

  /**
   * Faz check-in no local especificado
   */
  const doCheckin = useCallback(
    async (resourceId: number, observation?: string) => {
      // Validar que temos a localização GPS
      if (!geolocation.position?.latitude || !geolocation.position?.longitude) {
        // Tentar obter a localização primeiro
        geolocation.refresh();
        throw new Error('Localização GPS não disponível. Por favor, permita o acesso à localização.');
      }

      const now = new Date().toISOString().replace('T', ' ').slice(0, 19);

      const checkinData = {
        id_person: personId,
        id_resource: resourceId,
        datetime_checkin: now,
        latitude: geolocation.position.latitude,
        longitude: geolocation.position.longitude,
        observation,
        source: 'gps' as const,
      };

      // Se offline, salvar localmente
      if (!navigator.onLine) {
        const clientId = offline.addOfflineCheckin(checkinData);
        return { success: true, offline: true, clientId };
      }

      // Se online, enviar para o servidor
      try {
        const checkin = await mutation.checkin(checkinData);
        await activeCheckin.refresh();
        return { success: true, offline: false, checkin };
      } catch (err) {
        // Se falhar, salvar offline
        const clientId = offline.addOfflineCheckin(checkinData);
        return { success: false, offline: true, clientId, error: err };
      }
    },
    [personId, geolocation.position, mutation, activeCheckin.refresh, offline]
  );

  /**
   * Faz checkout do check-in ativo
   */
  const doCheckout = useCallback(
    async (observation?: string) => {
      if (!activeCheckin.data) {
        throw new Error('Nenhum check-in ativo');
      }

      const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
      const checkin = await mutation.checkout(activeCheckin.data.id, {
        datetime_checkout: now,
        observation,
      });
      await activeCheckin.refresh();
      return checkin;
    },
    [activeCheckin.data, mutation, activeCheckin.refresh]
  );

  /**
   * Busca locais próximos à posição atual
   */
  const searchNearby = useCallback(
    async (options: { radius?: number; limit?: number; search?: string } = {}) => {
      if (!geolocation.position) {
        throw new Error('Localização não disponível');
      }

      return nearbyLocations.searchNearby({
        latitude: geolocation.position.latitude,
        longitude: geolocation.position.longitude,
        radius: options.radius || 5000,
        limit: options.limit || 20,
        search: options.search,
      });
    },
    [geolocation.position, nearbyLocations]
  );

  return {
    // State
    position: geolocation.position,
    positionLoading: geolocation.loading,
    positionError: geolocation.error,
    positionSupported: geolocation.supported,
    positionRequested: geolocation.requested,
    activeCheckin: activeCheckin.data,
    activeCheckinLoading: activeCheckin.loading,
    activeCheckinError: activeCheckin.error,
    locations: nearbyLocations.locations,
    locationsLoading: nearbyLocations.loading,
    locationsError: nearbyLocations.error,
    mutationLoading: mutation.loading,
    mutationError: mutation.error,
    offlinePendingCount: offline.pendingCount,
    offlineSyncing: offline.syncing,
    // Actions
    refreshPosition: geolocation.refresh,
    refreshActiveCheckin: activeCheckin.refresh,
    doCheckin,
    doCheckout,
    searchNearby,
    searchByText: nearbyLocations.searchByText,
    resetLocations: nearbyLocations.reset,
    syncOffline: offline.syncOfflineCheckins,
    // Helpers
    formatDistance,
  };
}

export default useCheckin;
